import Button from '@shared/components/core/button/CoreButton';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import OrganizationMemberDetailsModel from '@shared/models/organizations/OrganizationMemberDetailsModel';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

// todo: refactor this for cleaner logic
export default function OrgBillingRedirectContent() {
    const { t } = useTranslation();
    const { organizationId, productId, managedOrgId } = useParams();
    const navigate = useNavigate();

    const openBillingLink = (billingLink: string | null, errorRedirect: string) => {
        if (billingLink) {
            window.open(billingLink, '_blank', 'noopener,noreferrer');
            navigate(-1); // redirect to previous page to maintain context
            useAlertsStore.getState().showSuccess(t('common:license.redirect.external'));
        } else {
            navigate(errorRedirect, { replace: true });
        }
    };

    const getBillingLinkForProduct = async (
        productId: string,
        org: OrganizationMemberDetailsModel,
        trialId: string | null
    ) => {
        let billingLink: string | null = null;

        if (org.organization.isReseller) {
            // reseller portal - purchase license keys
            billingLink = await useOrganizationStore
                .getState()
                .getOrganizationProductLink(org.organizationId, productId);
        } else if (org.organization.hasLicense) {
            // licensed org upgrade to a specific product
            billingLink = await useOrganizationStore
                .getState()
                .getBillingLinkForOrgByProduct(org.organizationId, productId);
        } else if (trialId !== null) {
            // trial upgrade link for specific product
            billingLink = await useOrganizationStore
                .getState()
                .getTrialUpgradeLinkForProduct(org.organizationId, trialId, productId);
        }

        return billingLink;
    };

    const getOrgBillingLink = async (
        org: OrganizationMemberDetailsModel,
        trialId: string | null,
        isStripeTrial: boolean
    ) => {
        let billingLink: string | null = null;
        const organizationId = org.organizationId;

        if (org.organization.hasLicense || isStripeTrial) {
            billingLink = await useOrganizationStore.getState().getBillingLink(organizationId);
        } else if (trialId !== null) {
            billingLink = await useOrganizationStore.getState().getTrialUpgradeLink(organizationId, trialId);
        }
        return billingLink;
    };

    const getTrial = (org: OrganizationMemberDetailsModel) => {
        // most recently active trial at the beginning
        const sortedTrials =
            org.organization.trials?.sort(
                (a, b) =>
                    new Date(b.expirationDate).setHours(0, 0, 0, 0) - new Date(a.expirationDate).setHours(0, 0, 0, 0)
            ) || [];
        return sortedTrials.length > 0 ? sortedTrials[0] : null;
    };

    const loadBillingLink = async () => {
        if (!organizationId || useOrganizationStore.getState().loading) return false;

        const org = useOrganizationStore.getState().getOrganizationMembership(organizationId);
        if (!org) return false;

        if (!(productId || (managedOrgId && org.canManageChildOrganizations) || org.isAdmin || org.canManageBilling)) {
            // billing permissions
            return false;
        }

        let billingLink: string | null = null;
        let errorRedirect = org.organization.hasLicense
            ? `/org/${organizationId}/billing/redirect/error`
            : `/org/${organizationId}/trial/redirect/error`;

        const trial = getTrial(org);
        const trialId = trial ? trial.id : null;
        const isStripeTrial =
            !org.organization.hasLicense && trial?.isAutoUpdate === true && trial.isCreatedFromStripe === true;

        const isActiveLicense =
            org.organization.hasLicense &&
            (!org.organization.licenseExpirationDate || new Date(org.organization.licenseExpirationDate) >= new Date());

        if (productId) {
            // product specific billing link
            billingLink = await getBillingLinkForProduct(productId, org, trialId);
        } else if (managedOrgId) {
            // reseller org managing subscription for managed org
            billingLink = await useOrganizationStore
                .getState()
                .getBillingLinkForManagedOrg(organizationId, managedOrgId);
            errorRedirect = `/org/${organizationId}/managed/${managedOrgId}/billing/redirect/error`;
        } else if (org.organization.customBillingUrl || isActiveLicense || isStripeTrial) {
            // org custom billing URL or licensed org is active
            billingLink = await getOrgBillingLink(org, trialId, isStripeTrial);
        } else {
            // redirect to explore billing page
            navigate(`/org/${organizationId}/billing/compare`, { replace: true });
            return true;
        }

        openBillingLink(billingLink, errorRedirect);
        return true;
    };

    useEffect(() => {
        const manageBillingLink = async () => {
            const hasBillingPermissions = await loadBillingLink();
            if (!hasBillingPermissions) {
                useAlertsStore.getState().showError(t('common:organization.canManageBilling.error'));
                navigate(-1);
            }
        };

        manageBillingLink();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    return (
        <Button startIcon="repeat" onClick={() => loadBillingLink()}>
            {t('common:reload')}
        </Button>
    );
}
